import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { 
  ArrowLeft,
  ArrowUpRight,
  Clock,
  CheckCircle2,
  XCircle,
  User,
  Trash2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminWithdrawals() {
  const navigate = useNavigate();
  const [withdrawals, setWithdrawals] = useState([]);
  const [users, setUsers] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [withdrawalsRes, usersRes] = await Promise.all([
        axios.get(`${API_URL}/admin/withdrawals`),
        axios.get(`${API_URL}/admin/users`)
      ]);
      setWithdrawals(withdrawalsRes.data);
      
      const usersMap = {};
      usersRes.data.forEach(user => {
        usersMap[user.id] = user;
      });
      setUsers(usersMap);
    } catch (error) {
      // Error silenced: 'Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Emit event to notify AdminLayout to refresh counts
  const notifyCountsChanged = () => {
    window.dispatchEvent(new CustomEvent('pendingCountsChanged'));
  };

  const handleApprove = async (withdrawalId) => {
    try {
      await axios.put(`${API_URL}/admin/withdrawals/${withdrawalId}/approve`);
      toast.success('Levantamento aprovado com sucesso');
      fetchData();
      notifyCountsChanged(); // Notify AdminLayout
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao aprovar levantamento');
    }
  };

  const handleApproveAndReinvest = async (withdrawalId, amount, userName) => {
    if (!window.confirm(`✅ APROBAR Y REINVERTIR\n\n¿Aprobar el retiro de ${formatCurrency(amount)} de ${userName} y automáticamente reinvertirlo?\n\nEsta acción:\n- Aprueba el retiro\n- Reinvierte el MISMO monto inmediatamente\n- El saldo del usuario NO cambia\n- Se registran ambas transacciones`)) return;
    
    try {
      const response = await axios.put(`${API_URL}/admin/withdrawals/${withdrawalId}/approve-and-reinvest`);
      toast.success(`Retiro aprobado y reinvertido: ${formatCurrency(response.data.reinvested_amount)}`);
      fetchData();
      notifyCountsChanged();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al aprobar y reinvertir');
    }
  };

  const handleReject = async (withdrawalId) => {
    if (!window.confirm('Tem certeza que deseja rejeitar este levantamento?')) return;
    
    try {
      await axios.put(`${API_URL}/admin/withdrawals/${withdrawalId}/reject`);
      toast.success('Levantamento rejeitado');
      fetchData();
      notifyCountsChanged(); // Notify AdminLayout
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao rejeitar levantamento');
    }
  };

  const handleDelete = async (withdrawalId, amount, userName) => {
    if (!window.confirm(`⚠️ ATENÇÃO: Tem certeza que deseja ELIMINAR PERMANENTEMENTE este levantamento de ${formatCurrency(amount)} de ${userName}?\n\nEsta ação não pode ser desfeita e irá:\n- Remover o levantamento do sistema\n- Reverter o saldo se já foi aprovado`)) return;
    
    try {
      await axios.delete(`${API_URL}/admin/withdrawals/${withdrawalId}`);
      toast.success('Levantamento eliminado permanentemente');
      fetchData();
      notifyCountsChanged();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao eliminar levantamento');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-PT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value || 0);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return (
          <span className="badge-pending flex items-center gap-1">
            <Clock className="w-3 h-3" />
            Pendente
          </span>
        );
      case 'approved':
        return (
          <span className="badge-approved flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3" />
            Aprovado
          </span>
        );
      case 'rejected':
        return (
          <span className="badge-rejected flex items-center gap-1">
            <XCircle className="w-3 h-3" />
            Rejeitado
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <div className="animate-fade-in" data-testid="admin-withdrawals-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={() => navigate('/admin')}
          className="w-10 h-10 rounded-xl bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-slate-400" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-white font-['Outfit']">Gestão de Levantamentos</h1>
          <p className="text-slate-400 text-sm">{withdrawals.filter(w => w.status === 'pending').length} pendentes</p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
        </div>
      ) : withdrawals.length > 0 ? (
        <div className="glass-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr className="bg-white/5">
                  <th>Cliente</th>
                  <th>IBAN</th>
                  <th>Montante</th>
                  <th>Data</th>
                  <th>Estado</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {withdrawals.map((withdrawal) => (
                  <tr key={withdrawal.id}>
                    <td>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-red-500/10 flex items-center justify-center">
                          <User className="w-4 h-4 text-red-400" />
                        </div>
                        <span className="text-white">{users[withdrawal.user_id]?.name || 'N/A'}</span>
                      </div>
                    </td>
                    <td className="text-slate-300 font-mono text-sm">
                      {users[withdrawal.user_id]?.iban || 'Não configurado'}
                    </td>
                    <td className="text-white font-semibold">{formatCurrency(withdrawal.amount)}</td>
                    <td className="text-slate-400">
                      {new Date(withdrawal.created_at).toLocaleDateString('pt-PT', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </td>
                    <td>{getStatusBadge(withdrawal.status)}</td>
                    <td>
                      <div className="flex items-center gap-2">
                        {withdrawal.status === 'pending' && (
                          <>
                            <Button
                              onClick={() => handleApprove(withdrawal.id)}
                              size="sm"
                              className="bg-emerald-500 hover:bg-emerald-600 rounded-lg h-8"
                              data-testid={`approve-withdrawal-${withdrawal.id}`}
                            >
                              Aprobar
                            </Button>
                            <Button
                              onClick={() => handleApproveAndReinvest(withdrawal.id, withdrawal.amount, users[withdrawal.user_id]?.name || 'Usuario')}
                              size="sm"
                              className="bg-blue-500 hover:bg-blue-600 rounded-lg h-8"
                              title="Aprobar el retiro y automáticamente reinvertir el mismo monto"
                              data-testid={`approve-reinvest-withdrawal-${withdrawal.id}`}
                            >
                              Aprobar + Reinvertir
                            </Button>
                            <Button
                              onClick={() => handleReject(withdrawal.id)}
                              size="sm"
                              variant="outline"
                              className="border-red-500/50 text-red-400 hover:bg-red-500/10 rounded-lg h-8"
                              data-testid={`reject-withdrawal-${withdrawal.id}`}
                            >
                              Rechazar
                            </Button>
                          </>
                        )}
                        <Button
                          onClick={() => handleDelete(withdrawal.id, withdrawal.amount, users[withdrawal.user_id]?.name || 'N/A')}
                          size="sm"
                          variant="ghost"
                          className="text-red-400 hover:bg-red-500/10 rounded-lg h-8 px-2"
                          title="Eliminar permanentemente"
                          data-testid={`delete-withdrawal-${withdrawal.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="glass-card p-12 text-center">
          <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
            <ArrowUpRight className="w-8 h-8 text-slate-500" />
          </div>
          <h3 className="text-lg font-semibold text-white mb-2">Sem levantamentos</h3>
          <p className="text-slate-400">Não há levantamentos registados.</p>
        </div>
      )}
    </div>
  );
}
